import React, { useState, useEffect } from 'react';
import { noteAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, isSameDay } from 'date-fns';
import toast from 'react-hot-toast';
import { StickyNote, Plus, Trash2, Edit2, X, Settings, Repeat, CheckCircle2, Clock, Image as ImageIcon, Phone, PlusCircle } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const REPEAT_LABEL = { none: 'One-time', daily: 'Daily', weekly: 'Weekly' };

function NoteFormModal({ note, defaultNumber, onClose, onSaved }) {
  const [form, setForm] = useState({
    text: note?.text || '',
    remindAt: note?.remindAt ? format(new Date(note.remindAt), "yyyy-MM-dd'T'HH:mm") : '',
    repeat: note?.repeat || 'none',
    mobileNumbers: note?.mobileNumbers?.length ? note.mobileNumbers : [''],
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(note?.imageUrl || '');
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);

  const setNumberAt = (i, value) => setForm(f => ({
    ...f, mobileNumbers: f.mobileNumbers.map((n, ni) => ni === i ? value : n),
  }));
  const addNumberRow = () => setForm(f => ({ ...f, mobileNumbers: [...f.mobileNumbers, ''] }));
  const removeNumberRow = (i) => setForm(f => ({
    ...f, mobileNumbers: f.mobileNumbers.filter((_, ni) => ni !== i),
  }));

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setRemoveImage(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview('');
    if (note?.imageUrl) setRemoveImage(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.text.trim() || !form.remindAt) { toast.error('Note text and reminder time are required'); return; }
    setSaving(true);
    try {
      const numbers = form.mobileNumbers.map(n => n.trim()).filter(Boolean);
      let payload;
      if (image) {
        payload = new FormData();
        payload.append('text', form.text);
        payload.append('remindAt', new Date(form.remindAt).toISOString());
        payload.append('repeat', form.repeat);
        // A repeated multipart field becomes an array server-side; an empty array needs
        // at least one (blank) entry so the field is still present — otherwise the server
        // can't tell "clear all numbers" apart from "field wasn't sent".
        if (numbers.length) numbers.forEach(n => payload.append('mobileNumbers', n));
        else payload.append('mobileNumbers', '');
        payload.append('image', image);
      } else {
        payload = {
          text: form.text, remindAt: new Date(form.remindAt).toISOString(), repeat: form.repeat,
          mobileNumbers: numbers, ...(removeImage ? { removeImage: true } : {}),
        };
      }
      const { data } = note ? await noteAPI.update(note._id, payload) : await noteAPI.create(payload);
      toast.success(note ? 'Note updated' : 'Note added');
      onSaved(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save note');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{note ? 'Edit Note' : 'New Note'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Note</label>
              <textarea className="form-control" rows={3} value={form.text} autoFocus
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                placeholder="What do you want to be reminded about?" />
            </div>
            <div className="form-group">
              <label className="form-label">Remind me at</label>
              <input type="datetime-local" className="form-control" value={form.remindAt}
                onChange={e => setForm(f => ({ ...f, remindAt: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Repeat</label>
              <select className="form-control" value={form.repeat}
                onChange={e => setForm(f => ({ ...f, repeat: e.target.value }))}>
                <option value="none">One-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Send to WhatsApp number(s)</label>
              {form.mobileNumbers.map((num, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="form-control" value={num}
                    onChange={e => setNumberAt(i, e.target.value)}
                    placeholder={defaultNumber ? `Default: ${defaultNumber}` : 'e.g. 9428040155'} />
                  {form.mobileNumbers.length > 1 && (
                    <button type="button" onClick={() => removeNumberRow(i)}
                      className="btn btn-secondary" style={{ padding: '0 10px', flexShrink: 0 }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addNumberRow}
                className="btn btn-secondary btn-sm" style={{ marginTop: 2 }}>
                <PlusCircle size={13} /> Add another number
              </button>
              <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Leave blank to use the default number set in Notes → Number.
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">Image (optional)</label>
              {imagePreview ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={imagePreview} alt="Note attachment" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, display: 'block' }} />
                  <button type="button" onClick={clearImage}
                    style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <X size={14} color="var(--c-ffffff)" />
                  </button>
                </div>
              ) : (
                <input type="file" accept="image/*" className="form-control" onChange={handleImagePick} />
              )}
              <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Sent along with the reminder as a WhatsApp image.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SettingsModal({ initialNumber, onClose, onSaved }) {
  const [number, setNumber] = useState(initialNumber);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await noteAPI.updateSettings({ whatsappNumber: number });
      toast.success('WhatsApp number saved');
      onSaved(data.data.whatsappNumber);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save number');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reminder WhatsApp Number</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Your WhatsApp number</label>
              <input className="form-control" value={number} onChange={e => setNumber(e.target.value)}
                placeholder="e.g. 9428040155" />
              <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Every note's reminder is sent here.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function NotesPage() {
  useDarkReady();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = async () => {
    try {
      const [{ data: notesRes }, { data: settingsRes }] = await Promise.all([
        noteAPI.getAll(), noteAPI.getSettings(),
      ]);
      setNotes(notesRes.data || []);
      setWhatsappNumber(settingsRes.data?.whatsappNumber || '');
    } catch { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await noteAPI.remove(id);
      setNotes(list => list.filter(n => n._id !== id));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete note');
    }
  };

  const upsertLocal = (saved) => {
    setNotes(list => {
      const exists = list.some(n => n._id === saved._id);
      const next = exists ? list.map(n => n._id === saved._id ? saved : n) : [...list, saved];
      return next.sort((a, b) => new Date(a.remindAt) - new Date(b.remindAt));
    });
    setShowForm(false);
    setEditing(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700 }}>Reminders</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
            {whatsappNumber ? `Reminders go to WhatsApp ${whatsappNumber}` : 'Set a WhatsApp number to start getting reminders'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowSettings(true)}>
            <Settings size={14} /> Number
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus size={14} /> New Note
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : notes.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <StickyNote size={32} />
          <h3>No notes yet</h3>
          <p>Add a note and a time — you'll get a WhatsApp reminder.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {notes.map(n => {
            const due = n.status === 'pending' && new Date(n.remindAt) <= new Date();
            const remindDate = new Date(n.remindAt);
            const dateColor = isSameDay(remindDate, new Date())
              ? 'var(--c-2563eb)'
              : remindDate < new Date() ? 'var(--c-dc2626)' : 'var(--c-16a34a)';
            return (
              <div key={n._id} className="card">
                <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  {n.imageUrl && (
                    <img src={n.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '.95rem', marginBottom: 6, wordBreak: 'break-word' }}>{n.text}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: dateColor, fontWeight: 600 }}>
                        <Clock size={12} /> {format(remindDate, 'dd MMM yyyy, h:mm a')}
                      </span>
                      {n.repeat !== 'none' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Repeat size={12} /> {REPEAT_LABEL[n.repeat]}
                        </span>
                      )}
                      {n.mobileNumbers?.length > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={12} /> {n.mobileNumbers.join(', ')}
                        </span>
                      )}
                      {n.createdBy?.name && (
                        <span style={{ fontStyle: 'italic' }}>by {n.createdBy.name}</span>
                      )}
                      {n.imageUrl && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <ImageIcon size={12} /> Image
                        </span>
                      )}
                      <span style={{
                        padding: '2px 8px', borderRadius: 12, fontWeight: 600,
                        background: n.status === 'sent' ? 'var(--cb-d1fae5)' : due ? 'var(--cb-fef3c7)' : 'var(--cb-e0e7ff)',
                        color: n.status === 'sent' ? 'var(--c-059669)' : due ? 'var(--c-d97706)' : 'var(--c-4338ca)',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        {n.status === 'sent' ? <><CheckCircle2 size={11} /> Sent</> : due ? 'Due' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  {(n.createdBy?._id || n.createdBy) === user?._id && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button
                        title="Edit" onClick={() => { setEditing(n); setShowForm(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, display: 'flex' }}
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        title="Delete" onClick={() => handleDelete(n._id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex' }}
                      >
                        <Trash2 size={15} color="var(--c-dc2626)" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <NoteFormModal note={editing} defaultNumber={whatsappNumber} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={upsertLocal} />
      )}
      {showSettings && (
        <SettingsModal initialNumber={whatsappNumber} onClose={() => setShowSettings(false)}
          onSaved={(num) => { setWhatsappNumber(num); setShowSettings(false); }} />
      )}
    </div>
  );
}
